import { Box, Dialog, DialogContent, MenuItem, TextField } from "@mui/material"
import { DesktopDatePicker } from "@mui/x-date-pickers-pro"
import { BoxFC, BoxFR } from "components/BoxCustom"
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand"
import PaperComponent from "components/PaperComponent"
import SearchButton from "components/buttons/SearchButton"
import { DialogFooter, DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import dayjs from "dayjs"
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { addIdForDataGrid } from "utils"
import { addSumRow, getSumColorClassName, sxSumColorClass } from "utils/highlightSumColor"

const WEEKDAYS_TH = ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"]

const MonthRecordDialog = ({ dialogOpen, setDialogOpen }) => {

  const { ax } = useContext(GlobalStateContext)
  const [month, setMonth] = useState(dayjs())
  const [mtChkTypId, setMtChkTypId] = useState(1)
  const [dataTable, setDataTable] = useState([])

  const modDataTable = useMemo(() => {
    const daysInMonth = dayjs(month).daysInMonth()
    const sumColNms = []
    for (let d = 1; d <= daysInMonth; d++) {
      sumColNms.push(`Day${d}`)
    }
    sumColNms.push("NumAprvTotal")
    return addSumRow({ dataTable, sumColNms, sumColTotal: "DrvNm" })
  }, [dataTable, month])
  
  const getData = useCallback((mth, typId) => {
    const postData = {
      MtChkDteSt: dayjs(mth).startOf("month").format("YYYY-MM-DD 00:00:00.000"),
      MtChkDteEn: dayjs(mth).endOf("month").format("YYYY-MM-DD 23:59:59.997"),
      MtChkTypId: typId,
    }
    ax.post("/maintenance/getCheckupMonthRecord", postData).then(value => {
      if (value.data) {
        const daysInMonth = dayjs(mth).daysInMonth()
        const withTotal = value.data.map(row => {
          let total = 0
          for (let d = 1; d <= daysInMonth; d++) {
            if (row[`Day${d}`] > 0) total += row[`Day${d}`]
          }
          return { ...row, NumAprvTotal: total }
        })
        setDataTable(addIdForDataGrid(withTotal, "DrvId"))
      }
    })
  }, [ax])

  useEffect(() => {
    if (dialogOpen) {
      getData(month, mtChkTypId)
    } else {
      setDataTable([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen])

  const columns = useMemo(() => {
    const daysInMonth = dayjs(month).daysInMonth()
    const cols = [
      { field: "No", headerName: "ลำดับ", width: 40, align:"center" },
      { field: "DrvCd", headerName: "รหัสพนักงาน", width: 100 },
      { field: "DrvNm", headerName: "พนักงานขับรถ", width: 180 },
      { field: "NName", headerName: "ชื่อเรียก", width: 100 },
    ]
    for (let d = 1; d <= daysInMonth; d++) {
      const dow = dayjs(month).date(d).day()
      const isSunday = dow === 0
      cols.push({
        field: `Day${d}`,
        headerName: `${d} ${WEEKDAYS_TH[dow]}`,
        width: 50,
        align: "center",
        headerAlign: "center",
        sortable: false,
        cellClassName: isSunday ? "sunday-cell" : "",
        headerClassName: isSunday ? "sunday-cell" : "",
        valueFormatter: ({ value, id }) => id === 0 ? value : value == null ? "" : value > 0 ? "✔" : "✘",
      })
    }
    cols.push({
      field: "NumAprvTotal",
      headerName: "รวม",
      width: 70,
      align: "center",
      headerAlign: "center",
    })
    return cols
  }, [month])

  return (
    <Dialog
      open={dialogOpen}
      onClose={(e, reason) => {
        if (reason === "backdropClick") return
        setDialogOpen(false)
      }}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth='xl'
    >
      <DialogHeader handleDialogClose={() => setDialogOpen(false)}
        title={"รายงานการตรวจรถประจำเดือน"}
        onDeleteClick={null}
      />
      <DialogContent>
        <BoxFC pt={2} height="70vh">
          <BoxFR>
            <Box flex={1}/>
            <TextField size="small" label="ประเภทการตรวจ" select value={mtChkTypId}
              onChange={e => setMtChkTypId(e.target.value)} sx={{ width: 180 }}>
              <MenuItem value={1}>รายวัน</MenuItem>
              <MenuItem value={2}>รายสัปดาห์</MenuItem>
              <MenuItem value={3}>รายเดือน</MenuItem>
            </TextField>
            <DesktopDatePicker
              label="เดือน"
              views={["year", "month"]}
              value={month}
              inputFormat="MM/YYYY"
              onChange={(newValue) => setMonth(newValue)}
              renderInput={(params) => <TextField {...params} size="small" sx={{ width: 150 }} />}
            />
            <SearchButton onClick={() => getData(month, mtChkTypId)} />
          </BoxFR>
          <BoxFC flex={1} sx={{
            "& .sunday-cell": { bgcolor: "#ffe5e5" },
            "& .MuiDataGrid-columnHeader": { px: 0.5 },
            "& .MuiDataGrid-cell": { px: 0.5 },
            ...sxSumColorClass,
          }}>
            <DataGridCellExpand
              hideFooter
              rows={modDataTable}
              columns={columns}
              getRowClassName={getSumColorClassName}
              experimentalFeatures={{ rowPinning: true }}
              pinnedRows={{ bottom: [modDataTable.find(row => row.id === 0)].filter(Boolean) }}
              initialState={{
                pinnedColumns: { right: ["NumAprvTotal"] },
              }}
            />
          </BoxFC>
        </BoxFC>
      </DialogContent>
      <DialogFooter okText={""}
        handleDialogClose={() => setDialogOpen(false)}
        handleDialogOk={null} />
    </Dialog>
  )
}

export default React.memo(MonthRecordDialog)
